// src/components/Sidebar/ApprovalSideBar.jsx
import { useState, useEffect } from "react";
import { MenuItem } from "react-pro-sidebar";
import { Box, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import "react-pro-sidebar/dist/css/styles.css";

// Icons
import ApprovalIcon from "@mui/icons-material/Approval";
import SecondApprov from "@mui/icons-material/HowToReg";
import VerifiedIcon from "@mui/icons-material/Verified";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PersonIcon from "@mui/icons-material/Person";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

// Custom stock-item icon
const ShieldCheckIcon = (props) => (
  <svg {...props} viewBox="0 0 75 64">
    <path
      d="M32 2L6 12v18c0 13.2 9.3 25.8 22 30 12.7-4.2 22-16.8 22-30V12L32 2z"
      fill="none"
      stroke="#1E2A38"
      strokeWidth="2"
    />
    <path d="M26 36l-6-6 4-4 4 4 12-12 4 4-16 18z" fill="#16D2F5" />
  </svg>
);

const Item = ({ title, to, icon, selected, setSelected, style }) => {
  const theme = useTheme();
  const colors = theme.palette;

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.text.primary,
        ...style,
        backgroundColor:
          selected === title ? colors.primary.main : "transparent",
        borderRadius: "10px",
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography
        style={{
          fontWeight: "bold",
          color: style?.color || colors.text.primary,
          fontSize: "16px",
        }}
      >
        {title}
      </Typography>
      <Link to={to} />
    </MenuItem>
  );
};

export default function ApprovalSideBar({
  isCollapsed,
  selected,
  setSelected,
}) {
  const theme = useTheme();
  const colors = theme.palette;
  const location = useLocation();
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");
  const role = localStorage.getItem("role");

  // State
  const [isApprovalOpen, setIsApprovalOpen] = useState(() => {
    const saved = localStorage.getItem("approvalSidebarOpen");
    return saved ? JSON.parse(saved) : false;
  });
  const [showApp, setShowApp] = useState(false);
  const [userData, setUserData] = useState([]);
  const [permissions, setPermissions] = useState({
    primaryApproval: false,
    secondaryApproval: false,
    finalApproval: false,
    primaryAppLed: false,
    finalAppLed: false,
    stkitmApp: false,
    adminApproval: false,
    userConfigApproval: false,
  });

  // Save sidebar open state to localStorage
  useEffect(() => {
    localStorage.setItem("approvalSidebarOpen", JSON.stringify(isApprovalOpen));
  }, [isApprovalOpen]);

  // Auto-expand sidebar if current route is in approval section
  useEffect(() => {
    const approvalRoutes = [
      "/Infinite/PrimaryApprov",
      "/Infinite/SecondaryApprov",
      "/Infinite/FinalApprov",
      "/Infinite/LedgerPrimaryApp",
      "/Infinite/LedgerFinalApp",
      "/Infinite/StkItemFinalApp",
      "/Infinite/AdminList",
      "/Infinite/ApprovalModuleUsers"
    ];
    
    if (approvalRoutes.includes(location.pathname) && !isApprovalOpen) {
      setIsApprovalOpen(true);
    }
  }, [location.pathname, isApprovalOpen]);

  // Toggle handler
  const handleApprovalToggle = () => setIsApprovalOpen((prev) => !prev);

  // Fetch data on mount
  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserData = async () => {
    try {
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + "/profile/info",
        { role },
        { headers: { Authorization: token } }
      );
      setUserData(data.user[0]);
      checkApprovalModule();
    } catch {
      console.error("Error fetching user data");
    }
  };

  const checkApprovalModule = async () => {
    try {
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + "/module/enabledModuleslist",
        { cmpid },
        { headers: { Authorization: token } }
      );

      const approvalModuleIsEnabled = Object.values(data).some(
        (mod) =>
          String(mod.cmp_id) === String(cmpid) &&
          mod.module_Name === "Approval Module" &&
          Number(mod.status ?? 1) === 1
      );

      setShowApp(approvalModuleIsEnabled);
    } catch (error) {
      console.error("Error fetching enabled modules:", error);
    }
  };

  // Set admin permissions or fetch granular permissions
  useEffect(() => {
    if (!userData?.role) return;

    if (userData.role === "admin") {
      setPermissions({
        ...permissions,
        userConfigApproval: true,
        adminApproval: true,
      });
    } else {
      fetchApprovalPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const fetchApprovalPermissions = async () => {
    try {
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + "/profile/approvalPerm",
        { cmpid },
        { headers: { Authorization: token } }
      );

      const newPermissions = {
        primaryApproval: false,
        secondaryApproval: false,
        finalApproval: false,
        primaryAppLed: false,
        finalAppLed: false,
        stkitmApp: false,
        adminApproval: true,
      };

      Object.values(data).forEach((perm) => {
        if (perm.cmp_id !== cmpid && perm.user_id !== userData.user_Id) return;

        if (perm.type === "Voucher") {
          if (perm.permission_Level_1 === "1")
            newPermissions.primaryApproval = true;
          if (perm.permission_Level_2 === "1")
            newPermissions.secondaryApproval = true;
          if (perm.permission_Level_3 === "1")
            newPermissions.finalApproval = true;
        }
        if (perm.type === "ledger") {
          if (perm.permission_Level_1 === "1")
            newPermissions.primaryAppLed = true;
          if (perm.permission_Level_2 === "1")
            newPermissions.finalAppLed = true;
        }
        if (perm.type === "stockitem" && perm.permission_Level_1 === "1") {
          newPermissions.stkitmApp = true;
        }
      });

      setPermissions((prev) => ({ ...prev, ...newPermissions }));
    } catch (error) {
      console.error("Error fetching approval permissions:", error);
    }
  };

  if (!showApp) return null;

  return (
    <Box
      sx={{
        // Custom scrollbar for nested content if needed
        "& .pro-inner-list-item": {
          "&::-webkit-scrollbar": {
            width: "1px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: "1px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: colors.primary.main,
            borderRadius: "1px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: colors.primary.dark,
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${colors.primary.main} transparent`,
        },
      }}
    >
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={handleApprovalToggle}
      >
        <VerifiedIcon
          fontSize="small"
          style={{
            marginLeft: "25px",
            color: colors.text.primary,
          }}
        />
        <Typography
          paddingLeft={isCollapsed ? "25%" : "0px"}
          color={colors.text.primary}
          style={{
            margin: "10px 0 5px 15px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          Approval Module
        </Typography>
        {isApprovalOpen ? (
          <ExpandLessIcon
            style={{
              marginLeft: "auto",
              color: colors.text.primary,
            }}
          />
        ) : (
          <ExpandMoreIcon
            style={{
              marginLeft: "auto",
              color: colors.text.primary,
            }}
          />
        )}
      </Box>

      {isApprovalOpen && (
        <Box style={{ marginLeft: "20px" }}>
          {permissions.primaryApproval && (
            <Item
              title="Primary Voucher"
              to="/Infinite/PrimaryApprov"
              icon={<ApprovalIcon fontSize="small" />}
              selected={selected}
              setSelected={setSelected}
              style={{ color: colors.text.secondary }}
            />
          )}

          {permissions.secondaryApproval && (
            <Item
              title="Secondary Voucher"
              to="/Infinite/SecondaryApprov"
              icon={<SecondApprov fontSize="small" />}
              selected={selected}
              setSelected={setSelected}
              style={{ color: colors.text.secondary }}
            />
          )}

          {permissions.finalApproval && (
            <Item
              title="Final Voucher"
              to="/Infinite/FinalApprov"
              icon={<TaskAltIcon fontSize="small" />}
              selected={selected}
              setSelected={setSelected}
              style={{ color: colors.text.secondary }}
            />
          )}

          {permissions.primaryAppLed && (
            <Item
              title="Primary Ledger"
              to="/Infinite/LedgerPrimaryApp"
              icon={<PersonIcon fontSize="small" />}
              selected={selected}
              setSelected={setSelected}
              style={{ color: colors.text.secondary }}
            />
          )}

          {permissions.finalAppLed && (
            <Item
              title="Final Ledger"
              to="/Infinite/LedgerFinalApp"
              icon={<PeopleAltIcon fontSize="small" />}
              selected={selected}
              setSelected={setSelected}
              style={{ color: colors.text.secondary }}
            />
          )}

          {permissions.stkitmApp && (
            <Item
              title="Final Stock Items"
              to="/Infinite/StkItemFinalApp"
              icon={<ShieldCheckIcon fontSize="small" />}
              selected={selected}
              setSelected={setSelected}
              style={{ color: colors.text.secondary }}
            />
          )}

          {permissions.adminApproval && (
            <Item
              title="Approval Report"
              to="/Infinite/AdminList"
              icon={<AssignmentIndIcon fontSize="small" />}
              selected={selected}
              setSelected={setSelected}
              style={{ color: colors.text.secondary }}
            />
          )}

          {permissions.userConfigApproval && (
            <Item
              title="Users Config"
              to="/Infinite/ApprovalModuleUsers"
              icon={<AccountCircleIcon fontSize="small" />}
              selected={selected}
              setSelected={setSelected}
              style={{ color: colors.text.secondary }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}